/**
 * Login.jsx
 * 
 * Authentication page with dual functionality:
 * - Student login: Enter existing credentials to access student dashboard
 * - Admin school creation: Create new schools or access admin panel
 * 
 * Features:
 * - Background video for immersive experience
 * - Input validation and error handling
 * - Automatic user data normalization
 * - Responsive design with custom styling
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertError, alertWarning } from '../utils/alerts';

export default function Login() {
  const navigate = useNavigate();
  
  // Form state
  const [gameCode, setGameCode] = useState(''); 
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');

  /**
   * Handle student login - authenticate existing user
   */
  const handleStudentLogin = async () => {
    const normalizedCode = (gameCode || '').toUpperCase();
    const normalizedName = ((userName || '').toUpperCase()).slice(0, 10);
    
    // Validation: Require at least 1 character for all fields
    if (!normalizedCode || normalizedCode.length < 1) {
      await alertWarning('Missing game code', 'Please enter a game code.');
      return;
    }
    if (!normalizedName || normalizedName.length < 1) {
      await alertWarning('Missing username', 'Please enter a username.');
      return;
    }
    if (!password || password.length < 1) {
      await alertWarning('Missing password', 'Please enter a password.');
      return;
    }
    
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          code: normalizedCode, 
          name: normalizedName, 
          password 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        await alertError('Login failed', errorData.error || 'Login failed');
        return;
      }

      const userData = await response.json();
      
      // Normalize server response for consistent client-side usage
      const normalizedUser = { 
        ...userData, 
        id: (userData.id || '').toUpperCase(), 
        code: (userData.code || userData.schoolId || '').toUpperCase() 
      };
      
      // Store user data and redirect based on role
      localStorage.setItem("hp_user", JSON.stringify(normalizedUser));
      navigate(userData.isAdmin ? "/admin/school" : "/home");
      
    } catch (error) {
      console.error('Login error:', error);
      await alertError('Login failed', 'Unable to connect to server. Please try again.');
    }
  };


  /**
   * Handle admin school creation - create new school or access existing as admin
   */
  const handleSchoolCreation = async () => {
    const normalizedCode = (gameCode || '').toUpperCase();
    const normalizedName = ((userName || '').toUpperCase()).slice(0, 10);
    
    // Validation: Require at least 1 character for all fields
    if (!normalizedCode || normalizedCode.length < 1) {
      await alertWarning('Missing game code', 'Please enter a game code.');
      return;
    }
    if (!normalizedName || normalizedName.length < 1) {
      await alertWarning('Missing username', 'Please enter a username.');
      return;
    }
    if (!password || password.length < 1) {
      await alertWarning('Missing password', 'Please enter a password.');
      return;
    }
    
    // Validation: Accept alphanumeric codes (letters and numbers only), no special characters
    // Allow variable length (1-10 chars) to support codes like 'AB12' or '1234'
    if (!/^[A-Za-z0-9]{1,10}$/.test(normalizedCode)) {
      await alertWarning(
        'Invalid game code', 
        'Game code must contain only letters and numbers (1-10 characters).'
      );
      return;
    }

  const user = {
    id: normalizedName.trim(),
    name: normalizedName.trim(),
    code: normalizedCode,
    isAdmin: true,
    house: null,
    points: 0,
    schoolId: normalizedCode,
  };

  // Check if school already exists to avoid overwriting the name
  let existingSchool = null;
  try {
  const checkRes = await fetch(`/api/school/${normalizedCode}`);
    if (checkRes.ok) {
      existingSchool = await checkRes.json();
    }
  } catch (e) {
    // School doesn't exist, continue
  }

  // First create or update the school and ensure creator is an admin student with a hashed password
  const res = await fetch(`/api/school/${normalizedCode}`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ 
    id: normalizedCode, 
    name: existingSchool?.name || "", // Preserve existing name or use empty string for new schools
    creatorId: user.id, 
    password 
  }),
});

  if (!res.ok) {
    const err = await res.json();
    await alertError('Failed', err.error || 'Failed to create school.');
    return;
  }

  // Then log in to ensure credentials are validated and we get a normalized user payload
  const loginRes = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code: normalizedCode, name: normalizedName, password }),
  });
  if (!loginRes.ok) {
    const err = await loginRes.json();
    await alertError('Login failed after creating school', err.error || 'Login failed');
    return;
  }
  const loginUser = await loginRes.json();
  const normalized = { ...loginUser, id: (loginUser.id || '').toUpperCase(), code: (loginUser.code || loginUser.schoolId || '').toUpperCase() };
  localStorage.setItem('hp_user', JSON.stringify(normalized));
  navigate('/admin/school');
};

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
      {/* Background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1,
        }}
      >
        <source src="/videos/background.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Foreground content */}
      <div
        style={{
          display: 'grid',
          gap: 16,
          justifyItems: 'center',
          height: '100%',
          alignContent: 'center',
          color: '#fff',
          textShadow: '0 2px 4px rgba(0,0,0,0.8)',
          zIndex: 0
        }}
      >
        <h1 style={{ fontSize: 25, fontFamily: "'Cinzel Decorative', serif",  marginBottom: 24 }}>Enter a world of magic</h1>

        <div style={{ width: '100%', maxWidth: 400, display: 'grid', gap: 12, justifyItems: 'center' }}>
          <label>
            <div>Game Code</div>
            <input
              value={gameCode}
              onChange={(e) => setGameCode((e.target.value || '').toUpperCase())}
              maxLength={10}
              placeholder="AB12"
              inputMode="text"
              style={{ ...inputStyle, textTransform: 'uppercase' }}
            />
          </label>

          <label>
            <div>Name</div>
            <input
              value={userName}
              onChange={(e) => setUserName(((e.target.value || '').toUpperCase()).slice(0, 10))}
              maxLength={10}
              placeholder="Hermione"
              style={{ ...inputStyle, textTransform: 'uppercase' }}
            />
          </label>

          <label>
            <div>Password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••"
              style={inputStyle}
            />
          </label>

          <button onClick={handleStudentLogin} style={primaryBtn}>Log In</button>
          <button onClick={handleSchoolCreation} style={secondaryBtn}>Create or Modify a School</button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #334155',
  background: 'rgba(15,23,42,0.8)',
  color: '#fff',
  outline: 'none',
  width: 'auto',
  minWidth: 300,
};

const primaryBtn = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid transparent',
  background: '#4f46e5',
  color: '#fff',
  cursor: 'pointer',
  width: 'auto',
  minWidth: 300,
};

const secondaryBtn = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #475569',
  background: 'rgba(0,0,0,0.4)',
  color: '#a0b4ff',
  cursor: 'pointer',
  width: 'auto',
  minWidth: 300,
};
